import React, { useState, useEffect, useRef, useCallback } from 'react'
import { makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import { FormattedMessage } from 'react-intl'
import useAudioPlayer from './hooks/useAudioPlayer'
import { CapeeshColors } from '../../../../assets/ColorPalette'
import SceneBackground from './SceneBackground'
import renderFormattedText from './utils/renderFormattedText'
import { playerMessages } from './CapeeshPlayer.i18n.Messages'

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        gap: 20,
        padding: '12px 20px 20px',
        overflow: 'auto',
        '@media (min-width: 600px) and (orientation: landscape)': {
            gap: 12,
            padding: '8px 16px 16px',
        },
        '@media (max-width: 599px)': {
            padding: '0 0 12px',
            gap: 12,
            overflow: 'hidden',
            position: 'relative',
        },
    },
    sceneContainer: {
        width: '100%',
        maxWidth: 520,
        flexShrink: 0,
        minHeight: 100,
        borderRadius: 16,
        '@media (min-width: 600px) and (orientation: landscape)': {
            maxWidth: 'min(520px, 45vh)',
        },
        '@media (max-width: 599px)': {
            maxWidth: 'none',
            borderRadius: 0,
            flex: 1,
        },
    },
    textContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        maxWidth: 480,
        width: '100%',
        flexShrink: 0,
        '@media (max-width: 599px)': {
            padding: '0 12px',
            maxWidth: '100%',
        },
    },
    titleText: {
        fontFamily: 'Rubik',
        fontSize: 22,
        fontWeight: 600,
        color: '#FFFFFF',
        textAlign: 'center',
    },
    bodyText: {
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 400,
        lineHeight: '160%',
        color: 'rgba(255, 255, 255, 0.9)',
        textAlign: 'center',
    },
    timerBar: {
        width: '60%',
        maxWidth: 300,
        height: 4,
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        borderRadius: 2,
        overflow: 'hidden',
        flexShrink: 0,
    },
    timerFill: {
        height: '100%',
        backgroundColor: CapeeshColors.GreenBright,
        transition: 'width 100ms linear',
    },
    skipButton: {
        padding: '10px 32px',
        borderRadius: 24,
        border: '2px solid rgba(255, 255, 255, 0.4)',
        background: 'transparent',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 500,
        cursor: 'pointer',
        opacity: 0.7,
        transition: 'opacity 200ms ease',
        flexShrink: 0,
        '&:hover': {
            opacity: 1,
            borderColor: 'rgba(255, 255, 255, 0.7)',
        },
        '@media (max-width: 599px)': {
            padding: '8px 24px',
            fontSize: 13,
        },
    },
}))

const WatchNode = ({ node, previousNode, imageMap, audioMap, onNodeComplete, isNativeSpeaker = false, l2LanguageId }) => {
    const classes = useStyles()
    const audioPlayer = useAudioPlayer()
    const [elapsed, setElapsed] = useState(0)
    const timerRef = useRef(null)
    const startTime = useRef(Date.now())

    const duration = node.nodeLengthSeconds || 5

    const finish = useCallback(() => {
        if (timerRef.current) clearInterval(timerRef.current)
        audioPlayer.stopAudio()
        onNodeComplete({
            nodeId: node.nodeId,
            chosenAlternativeIndex: 0,
        })
    }, [node.nodeId, onNodeComplete, audioPlayer])

    useEffect(() => {
        const bgAudioUrl = node.backgroundAudioUrl
            ? audioMap[node.backgroundAudioUrl]
            : null
        if (bgAudioUrl) {
            audioPlayer.playAudio(bgAudioUrl)
        }

        timerRef.current = setInterval(() => {
            const now = Date.now()
            const elapsedSec = (now - startTime.current) / 1000
            setElapsed(elapsedSec)

            if (elapsedSec >= duration) {
                clearInterval(timerRef.current)
                finish()
            }
        }, 100)

        return () => {
            if (timerRef.current) clearInterval(timerRef.current)
            audioPlayer.stopAudio()
        }
    }, [])

    const progressPct = Math.min(100, (elapsed / duration) * 100)

    const texts = (node.texts || []).filter(
        (t) => !isNativeSpeaker || !l2LanguageId || t.textLanguageId === l2LanguageId || !t.textLanguageId
    )

    return (
        <div className={classes.container}>
            <div className={classes.sceneContainer}>
                <SceneBackground
                    node={node}
                    previousNode={previousNode}
                    imageMap={imageMap}
                    duration={duration}
                />
            </div>

            {texts.length > 0 && (
                <div className={classes.textContainer}>
                    {texts.map((txt, idx) => (
                        <Typography
                            key={idx}
                            className={
                                txt.textType === 1 || txt.textType === 2
                                    ? classes.titleText
                                    : classes.bodyText
                            }
                        >
                            {renderFormattedText(txt.text)}
                        </Typography>
                    ))}
                </div>
            )}

            <div className={classes.timerBar}>
                <div className={classes.timerFill} style={{ width: `${progressPct}%` }} />
            </div>

            <button className={classes.skipButton} onClick={finish}>
                <FormattedMessage {...playerMessages.skip} />
            </button>
        </div>
    )
}

export default WatchNode
